import React, { useState, useEffect, ChangeEvent } from "react";
import { Button, Segment } from "semantic-ui-react";
import { useTranslation } from "react-i18next";

import Styles from "./SleepingCustomer.module.scss";
import Heading from "./Heading/Heading";
import Input from "@/shared/components/Input/Input";
import SleepingCustomerRow from "./SleepingCustomerRow/SleepingCustomerRow";
import { CategoryT } from "@/changeservice/components/MissingData/types";
import Confirmation from "./Confirmation/Confirmation";
import SuccessfullySaved from "../SleepingCustomer/SuccessfullySaved/SuccessfullySaved";
import i18n from "@/i18n";
import { AT_LEAST_4_DIGITS, formatDate } from "@/changeservice/misc/utils";
import { SleepingCustomerAddress, SleepingCustomerT } from "./ConnectedSleepingCustomer";
import Responsive from "@/cms/components/Common/Responsive";
import validations from "@/shared/validations/validations";
import DateInput from "./DateInput/DateInput";

export const validateCustomerNumber = (customerNumber: string | null | undefined, prevCustomerNumber: string) => {
  if (customerNumber && !AT_LEAST_4_DIGITS.test(customerNumber)) return i18n.t("validation:validCustomerNumber");
  if (customerNumber && prevCustomerNumber) {
    if (customerNumber === prevCustomerNumber) {
      return i18n.t("validation:customerNumberSameAsPrevCustomerNumer", { prevCustomerNumber });
    }
  }
  return customerNumber && validations.lengthIsLessThan(30)(customerNumber);
};

type wasMissingSleepingCustomerT = {
  customerNumber?: boolean;
  startOfDeliveryDate?: boolean;
  contractEndDate?: boolean;
};

type Props = {
  category: CategoryT;
  address: SleepingCustomerAddress;
  counterNumber: string;
  sleepingCustomer: SleepingCustomerT;
  onSave: (values: SleepingCustomerT) => void;
  isUpdating: boolean;
  prevCustomerNumber: string;
  tarifName: string;
};

const SleepingCustomer = ({
  category,
  address,
  counterNumber,
  sleepingCustomer,
  isUpdating,
  onSave,
  prevCustomerNumber,
  tarifName,
}: Props) => {
  const { t } = useTranslation("sleepingCustomer");

  const [wasMissing] = useState<wasMissingSleepingCustomerT>({
    customerNumber: sleepingCustomer.customerNumber === null,
    startOfDeliveryDate: sleepingCustomer.startOfDeliveryDate === null,
    contractEndDate: sleepingCustomer.contractEndDate === null,
  });

  const [values, setValues] = useState<SleepingCustomerT>(sleepingCustomer);
  const [dateValidation, setDateValidation] = useState({ error: false, errMsg: "" as string | undefined });
  const onChange = (field: string, value: [string, string, string] | string) =>
    setValues({ ...values, [field]: value });

  const customerNumberError =
    values.customerNumber && validateCustomerNumber(values.customerNumber, prevCustomerNumber);

  const customerErrorClass = customerNumberError ? Styles.customerNumberError : Styles.customerNumber;

  useEffect(() => {
    if (values.startOfDeliveryDate && values.contractEndDate) {
      if (values.startOfDeliveryDate === values.contractEndDate) {
        setDateValidation({
          error: true,
          errMsg: t("startOfDeliveryDateAndcontractEndDateMatch"),
        });
      } else {
        setDateValidation({ error: false, errMsg: undefined });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.startOfDeliveryDate, values.contractEndDate]);

  const isSleepingDataFilled =
    sleepingCustomer.customerNumber !== null &&
    sleepingCustomer.startOfDeliveryDate !== null &&
    sleepingCustomer.contractEndDate !== null;

  const isSaveDisabled = () => {
    if (wasMissing.customerNumber && (!values.customerNumber || !!customerNumberError)) return true;
    if (wasMissing.startOfDeliveryDate && !values.startOfDeliveryDate) return true;
    if (wasMissing.contractEndDate && !values.contractEndDate) return true;
    if (values.contractEndDate === values.startOfDeliveryDate) return true;
    if (prevCustomerNumber === values.customerNumber) return true;
    return false;
  };

  const categoryName = category?.categoryName;

  const wasSaved = {
    customerNumber: !!values.customerNumber && values.customerNumber === sleepingCustomer.customerNumber,
    startOfDeliveryDate:
      !!values.startOfDeliveryDate && values.startOfDeliveryDate === sleepingCustomer.startOfDeliveryDate,
    contractEndDate: !!values.contractEndDate && values.contractEndDate === sleepingCustomer.contractEndDate,
  };

  return (
    <div className={Styles.formWrapper}>
      <Responsive minWidth={Responsive.onlyTablet.minWidth}>
        <div
          className="backgroundContainer landing-page-background-image new-publicbg"
          style={{
            backgroundImage: "url('https://cms.dev-remind.me/app/uploads/2019/12/tarifoptimierung_bg.jpg')",
          }}
        >
          <div className={Styles.background} />
        </div>
      </Responsive>
      <Segment attached className={Styles.formContainer}>
        {isSleepingDataFilled ? (
          <Confirmation category={category} address={address} />
        ) : (
          <>
            <Heading categoryName={categoryName} address={address} counterNumber={counterNumber} />
            {wasMissing.customerNumber && (
              <SleepingCustomerRow categoryName={categoryName} field="customerNumber" tarifName={tarifName}>
                {wasSaved.customerNumber ? (
                  <SuccessfullySaved>{values.customerNumber}</SuccessfullySaved>
                ) : (
                  <Input
                    placeholder={t("customerNumber.placeholder")}
                    value={values.customerNumber ?? ""}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => onChange("customerNumber", e.target.value)}
                    errorMessage={customerNumberError || ""}
                    validate={validations.isCustomerNumber}
                    className={customerErrorClass}
                  />
                )}
              </SleepingCustomerRow>
            )}
            <br />
            {wasMissing.startOfDeliveryDate && (
              <SleepingCustomerRow categoryName={categoryName} field="startOfDeliveryDate" tarifName={tarifName}>
                {wasSaved.startOfDeliveryDate ? (
                  <SuccessfullySaved>{formatDate(values.startOfDeliveryDate)}</SuccessfullySaved>
                ) : (
                  <DateInput
                    name="startOfDeliveryDate"
                    value={values.startOfDeliveryDate}
                    onChange={(value) => onChange("startOfDeliveryDate", value || "")}
                    error={dateValidation.error}
                    errorMessage={dateValidation.errMsg}
                    className={Styles.contractEndDate}
                  />
                )}
              </SleepingCustomerRow>
            )}
            <br />
            {wasMissing.contractEndDate && (
              <SleepingCustomerRow categoryName={categoryName} field="contractEndDate" tarifName={tarifName}>
                {wasSaved.contractEndDate ? (
                  <SuccessfullySaved>{formatDate(values.contractEndDate)}</SuccessfullySaved>
                ) : (
                  <DateInput
                    name={"contractEndDate"}
                    value={values.contractEndDate}
                    onChange={(value) => onChange("contractEndDate", value || "")}
                  />
                )}
              </SleepingCustomerRow>
            )}
            <div className={Styles.button}>
              <Button
                loading={isUpdating}
                negative
                className={Styles.cta}
                role="save"
                disabled={isSaveDisabled()}
                onClick={() => onSave(values)}
              >
                {t("cta")}
              </Button>
            </div>
          </>
        )}
      </Segment>
    </div>
  );
};

export default SleepingCustomer;
